import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Trash2, LogOut, ArrowLeft, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vps_admin_token";

const LEAD_COLS = [
  ["created_at", "Data"], ["tipo_richiesta", "Richiesta"], ["nome", "Nome"], ["azienda", "Azienda"],
  ["email", "Email"], ["ruolo", "Ruolo"], ["settore", "Settore"], ["consumo_annuo", "Consumo"],
  ["temperatura_processo", "Temperatura"], ["messaggio", "Messaggio"],
];

function exportCsv(leads) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const rows = [LEAD_COLS.map(([, l]) => esc(l)).join(";")];
  leads.forEach((lead) => rows.push(LEAD_COLS.map(([k]) => esc(k === "created_at" ? new Date(lead[k]).toLocaleString("it-IT") : lead[k])).join(";")));
  const blob = new Blob(["﻿" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `vps-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("lead");
  const [leads, setLeads] = useState([]);
  const [news, setNews] = useState([]);
  const [newsForm, setNewsForm] = useState({ titolo: "", data: "", testo: "", link: "", tag: "", titolo_en: "", testo_en: "" });

  const headers = { "X-Admin-Token": token };

  const load = useCallback(async (t) => {
    try {
      const [l, n] = await Promise.all([
        axios.get(`${API}/leads`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/news`),
      ]);
      setLeads(l.data);
      setNews(n.data);
      setAuthed(true);
      sessionStorage.setItem(TOKEN_KEY, t);
    } catch {
      setAuthed(false);
      toast.error("Token non valido");
    }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const addNews = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/news`, { ...newsForm, link: newsForm.link || null, tag: newsForm.tag || null }, { headers });
      toast.success("News pubblicata");
      setNewsForm({ titolo: "", data: "", testo: "", link: "", tag: "", titolo_en: "", testo_en: "" });
      const n = await axios.get(`${API}/news`);
      setNews(n.data);
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const deleteNews = async (id) => {
    try {
      await axios.delete(`${API}/news/${id}`, { headers });
      setNews(news.filter((n) => n.id !== id));
      toast.success("News eliminata");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={(e) => { e.preventDefault(); load(token); }} className="glass rounded-2xl p-10 w-full max-w-sm">
          <img src="/assets/logo.png" alt="VPS Group" className="h-10 w-auto mb-6" />
          <h1 className="font-display font-bold text-2xl">Area riservata</h1>
          <p className="mt-2 text-sm text-steel">Inserisci il token amministratore.</p>
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} className="field mt-6" placeholder="Token" data-testid="admin-token-input" />
          <button type="submit" className="btn-primary w-full justify-center mt-4" data-testid="admin-login-button">Accedi</button>
          <a href="/" className="mt-5 inline-flex items-center gap-1.5 text-xs text-steel hover:text-volt" data-testid="admin-back-link"><ArrowLeft size={12} /> Torna al sito</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 py-10 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src="/assets/logo.png" alt="VPS Group" className="h-9 w-auto" />
          <h1 className="font-display font-bold text-2xl">Area admin</h1>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" className="btn-outline !py-2 !px-4 !text-xs" data-testid="admin-home-link"><ArrowLeft size={14} /> Sito</a>
          <button onClick={logout} className="btn-outline !py-2 !px-4 !text-xs" data-testid="admin-logout-button"><LogOut size={14} /> Esci</button>
        </div>
      </div>

      <div className="mt-8 flex gap-2 border-b border-white/10">
        {[["lead", `Lead (${leads.length})`], ["news", `News (${news.length})`]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === key ? "border-volt text-white" : "border-transparent text-steel hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "lead" && (
        <div className="mt-8" data-testid="admin-leads-panel">
          <div className="flex justify-between items-center mb-5">
            <p className="text-sm text-steel">{leads.length} richieste raccolte dal sito</p>
            <button onClick={() => exportCsv(leads)} disabled={leads.length === 0} className="btn-primary !py-2.5 !px-5 !text-xs disabled:opacity-50" data-testid="export-csv-button">
              <Download size={14} /> Esporta CSV
            </button>
          </div>
          <div className="glass rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-steel">
                  {LEAD_COLS.map(([k, l]) => <th key={k} className="px-4 py-3 font-medium">{l}</th>)}
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`lead-row-${lead.id}`}>
                    {LEAD_COLS.map(([k]) => (
                      <td key={k} className="px-4 py-3 text-steel align-top max-w-[220px] truncate">
                        {k === "created_at" ? new Date(lead[k]).toLocaleString("it-IT") : lead[k] || "—"}
                      </td>
                    ))}
                  </tr>
                ))}
                {leads.length === 0 && <tr><td colSpan={LEAD_COLS.length} className="px-4 py-10 text-center text-steel">Nessun lead ancora.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "news" && (
        <div className="mt-8 grid lg:grid-cols-2 gap-8" data-testid="admin-news-panel">
          <form onSubmit={addNews} className="glass rounded-2xl p-7 space-y-5">
            <h2 className="font-display font-semibold text-lg">Nuova news</h2>
            <input required value={newsForm.titolo} onChange={(e) => setNewsForm({ ...newsForm, titolo: e.target.value })} className="field" placeholder="Titolo *" data-testid="news-titolo-input" />
            <div className="grid grid-cols-2 gap-4">
              <input required value={newsForm.data} onChange={(e) => setNewsForm({ ...newsForm, data: e.target.value })} className="field" placeholder="Data (es. Luglio 2026) *" data-testid="news-data-input" />
              <input value={newsForm.tag} onChange={(e) => setNewsForm({ ...newsForm, tag: e.target.value })} className="field" placeholder="Tag (es. Acceleratori)" data-testid="news-tag-input" />
            </div>
            <textarea required value={newsForm.testo} onChange={(e) => setNewsForm({ ...newsForm, testo: e.target.value })} rows={4} className="field resize-none" placeholder="Testo *" data-testid="news-testo-input" />
            <input value={newsForm.titolo_en} onChange={(e) => setNewsForm({ ...newsForm, titolo_en: e.target.value })} className="field" placeholder="Titolo EN (opzionale)" data-testid="news-titolo-en-input" />
            <textarea value={newsForm.testo_en} onChange={(e) => setNewsForm({ ...newsForm, testo_en: e.target.value })} rows={3} className="field resize-none" placeholder="Testo EN (opzionale)" data-testid="news-testo-en-input" />
            <input value={newsForm.link} onChange={(e) => setNewsForm({ ...newsForm, link: e.target.value })} className="field" placeholder="Link esterno (opzionale)" data-testid="news-link-input" />
            <button type="submit" className="btn-primary w-full justify-center" data-testid="news-publish-button"><Plus size={16} /> Pubblica</button>
          </form>
          <div className="space-y-4">
            {news.map((item) => (
              <div key={item.id} className="glass rounded-2xl p-6 flex justify-between gap-4" data-testid={`admin-news-item-${item.id}`}>
                <div>
                  <p className="text-xs text-steel">{item.data}{item.tag ? ` — ${item.tag}` : ""}</p>
                  <h3 className="mt-1 font-display font-semibold">{item.titolo}</h3>
                  <p className="mt-1 text-sm text-steel line-clamp-2">{item.testo}</p>
                </div>
                <button onClick={() => deleteNews(item.id)} className="text-steel hover:text-red-400 transition-colors shrink-0" data-testid={`news-delete-${item.id}`} aria-label="Elimina">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {news.length === 0 && <p className="text-sm text-steel">Nessuna news pubblicata.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
